import React from 'react';
import { FileText, MousePointerClick, Zap, Building2, Car, PaintBucket, Hammer } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../mock';
import { useNavigate } from 'react-router-dom';

const QuoteOptions = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Professional image of home with realistic hail and wind damage
  const solaInsuranceImage = "https://static.prod-images.emergentagent.com/jobs/dc74b56d-b401-41b5-907f-23da31e328be/images/491ba772e1ea9aaaf67bb05f720b7fbbba53cb4026bba2033b48178d0eeab61e.jpeg";

  const quoteOptions = {
    en: [
      {
        title: 'Auto Personal Quote',
        description: 'Get instant personal auto insurance quotes',
        icon: Car,
        link: '/quote/auto',
        color: 'from-red-600 to-red-700',
        isInternal: true
      },
      {
        title: 'Wind/Hail Coverage Quote',
        description: 'Wind And Hail Insurance Quote',
        icon: Zap,
        link: companyInfo.quoteLinks.sola,
        color: 'from-green-600 to-green-700',
        isInternal: false
      },
      {
        title: 'Click Insurance Portal',
        description: 'Car insurance 24/7 with just one click',
        icon: MousePointerClick,
        link: companyInfo.quoteLinks.clickInsurance,
        color: 'from-blue-600 to-blue-700',
        isInternal: false
      },
      {
        title: 'Commercial Insurance',
        description: 'Get comprehensive business insurance coverage',
        icon: Building2,
        link: '/products/business',
        color: 'from-purple-600 to-purple-700',
        isInternal: true
      }
    ],
    es: [
      {
        title: 'Cotización Auto Personal',
        description: 'Obtenga cotizaciones instantáneas de seguro de auto personal',
        icon: Car,
        link: '/quote/auto',
        color: 'from-red-600 to-red-700',
        isInternal: true
      },
      {
        title: 'Cotización Viento/Granizo',
        description: 'Cotización de Seguro contra Viento y Granizo',
        icon: Zap,
        link: companyInfo.quoteLinks.sola,
        color: 'from-green-600 to-green-700',
        isInternal: false
      },
      {
        title: 'Portal Click Insurance',
        description: 'Seguro de auto 24/7 con solo un clic',
        icon: MousePointerClick,
        link: companyInfo.quoteLinks.clickInsurance,
        color: 'from-blue-600 to-blue-700',
        isInternal: false
      },
      {
        title: 'Seguro Comercial',
        description: 'Obtenga cobertura integral de seguro comercial',
        icon: Building2,
        link: '/products/business',
        color: 'from-purple-600 to-purple-700',
        isInternal: true
      }
    ]
  };

  const options = quoteOptions[language];

  const handleClick = (option, e) => {
    if (option.isInternal) {
      e.preventDefault();
      navigate(option.link);
    }
  };

  return (
    <section id="quote-options" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' 
              ? 'Get Your Free Quote Now' 
              : 'Obtenga Su Cotización Gratis Ahora'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {language === 'en'
              ? 'Choose your preferred way to get a quote. We offer multiple convenient options to find the best coverage for you.'
              : 'Elija su forma preferida de obtener una cotización. Ofrecemos múltiples opciones convenientes para encontrar la mejor cobertura para usted.'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {options.map((option, index) => {
            const Icon = option.icon;
            const isSolaInsurance = option.title === 'Wind/Hail Coverage Quote' || option.title === 'Cotización Viento/Granizo';
            
            // Special design for Sola Insurance with background image
            if (isSolaInsurance) {
              return (
                <a
                  key={index}
                  href={option.link}
                  target={option.isInternal ? undefined : "_blank"}
                  rel={option.isInternal ? undefined : "noopener noreferrer"}
                  onClick={(e) => handleClick(option, e)}
                  className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
                >
                  {/* Background Image with Overlay */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                      backgroundImage: `url(${solaInsuranceImage})`,
                      backgroundPosition: 'center center'
                    }}
                  >
                    {/* Dark overlay for text legibility */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-8 flex flex-col h-full min-h-[380px] justify-between">
                    {/* Icon */}
                    <div className={`w-16 h-16 bg-gradient-to-br ${option.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Text Content */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-lg">
                        {option.title}
                      </h3>
                      <p className="text-white/90 leading-relaxed mb-6 drop-shadow-md">
                        {option.description}
                      </p>
                    </div>

                    {/* CTA Button */}
                    <div className={`inline-flex items-center gap-2 text-white bg-gradient-to-r ${option.color} px-6 py-3 rounded-full font-semibold group-hover:gap-3 transition-all shadow-xl`}>
                      {language === 'en' ? 'Get Quote' : 'Obtener Cotización'}
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </a>
              );
            }
            
            // Standard design for other cards
            const isCommercialInsurance = option.title === 'Commercial Insurance' || option.title === 'Seguro Comercial';
            
            return (
              <a
                key={index}
                href={option.link}
                target={option.isInternal ? undefined : "_blank"}
                rel={option.isInternal ? undefined : "noopener noreferrer"}
                onClick={(e) => handleClick(option, e)}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                {/* Gradient Top Border */}
                <div className={`h-2 bg-gradient-to-r ${option.color}`}></div>
                
                <div className="p-8">
                  {/* Icon with Painter & Carpentry Badge for Commercial Insurance */}
                  <div className="relative mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${option.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Painter & Carpentry Icon Badge for Commercial Insurance */}
                    {isCommercialInsurance && (
                      <div className="absolute -bottom-2 -right-2 flex gap-1">
                        {/* Paint Bucket Icon */}
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                          <PaintBucket className="w-4 h-4 text-white" />
                        </div>
                        {/* Hammer Icon */}
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                          <Hammer className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors">
                    {option.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {option.description}
                  </p>

                  {/* CTA Button */}
                  <div className={`inline-flex items-center gap-2 text-white bg-gradient-to-r ${option.color} px-6 py-3 rounded-full font-semibold group-hover:gap-3 transition-all`}>
                    {language === 'en' ? 'Get Quote' : 'Obtener Cotización'}
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>

                {/* Decorative Element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-16 -mt-16 opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
              </a>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-blue-50 rounded-2xl p-8 max-w-2xl">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              {language === 'en' 
                ? 'Need Help Choosing?' 
                : '¿Necesita Ayuda Para Elegir?'}
            </h4>
            <p className="text-gray-600 mb-4">
              {language === 'en'
                ? 'Our expert agents are here to guide you through the process and help you find the perfect coverage.'
                : 'Nuestros agentes expertos están aquí para guiarlo a través del proceso y ayudarlo a encontrar la cobertura perfecta.'}
            </p>
            <a 
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-800 transition-colors"
            >
              📞 {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteOptions;